import axios from 'axios';
import _ from 'lodash';

function toParams(params) {
  if (!params || _.isEmpty(params)) {
    return '';
  }
  const result = Object.entries(params).map(
    ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
  );
  return `?${result.join('&')}`;
}

const globalLanguage = 'IFS';

class HttpDataApi {
  constructor(baseURL = process.env.REACT_APP_DATA_API_URL) {
    this.baseURL = baseURL;
    this.isGlobal = false;

    this.httpClient = axios.create({
      baseURL,
      headers: { 'content-type': 'application/json', 'X-Frame-Options': 'deny' }
    });
  }

  setAuthToken = token => {
    this.httpClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  };

  removeAuthToken = () => {
    this.httpClient.defaults.headers.common.Authorization = undefined;
  };

  setMarket = market => {
    this.market = market;
  };

  setIsGlobalUser = () => {
    this.isGlobal = true;
  };

  setIsServiceOfficeUser = () => {
    this.isGlobal = false;
  };

  getOrganization = () => (this.isGlobal ? 'IFS' : this.market);

  // Config
  fetchConfigs = () => this.httpClient.get(`${this.getOrganization()}/config`);

  // Review market config
  fetchMarketConfigs = marketCode =>
    this.httpClient.get(`${marketCode}/config`);

  fetchGlobalConfigs = () => this.httpClient.get('IFS/config');

  // Dishes
  fetchDishes = filter => {
    // console.log(filter,"use dishesh")
    let param = '';
    if (filter && filter !== undefined) {
      param = `?status=${filter}`;
    } else {
      param = '';
    }
    return this.httpClient.get(
      `dppsi/${this.getOrganization()}/dishes${param}`
    );
  };

  fetchExcludedDishes = () =>
    this.httpClient.get(`dppsi/${this.getOrganization()}/dishes/exclude`);

  fetchDish = formulaCode =>
    this.httpClient.get(
      `dppsi/${this.getOrganization()}/dishes/${formulaCode}`
    );

  excludeDishes = formulaCodes =>
    this.httpClient.post(
      `dppsi/${this.getOrganization()}/dishes/exclude`,
      formulaCodes
    );

  includeDishes = formulaCodes =>
    this.httpClient.post(
      `dppsi/${this.getOrganization()}/dishes/include`,
      formulaCodes
    );

  // Dish Ingredients
  fetchDishIngredients = id =>
    this.httpClient.get(
      `dppsi/${this.getOrganization()}/ingredientEntries?dish=${id}`
    );

  // Ingredients
  fetchIngredients = () =>
    this.httpClient.get(`dppsi/${this.getOrganization()}/ingredients`);

  // fetchMarketIngredients = (marketCode) =>
  // this.httpClient.get(`dppsi/${this.getOrganization()}/ingredients`);
  // this.httpClient.get(`dppsi/${marketCode}/ingredients`);

  fetchExcludedIngredients = () =>
    this.httpClient.get(`dppsi/${this.getOrganization()}/ingredients/exclude`);

  fetchIngredient = itemCode =>
    this.httpClient.get(
      `dppsi/${this.getOrganization()}/ingredients/${itemCode}`
    );

  fetchGlobalIngredients = () =>
    this.httpClient.get(`dppsi/${this.getOrganization()}/ingredients/global`);

  fetchMarketName = () =>
    this.httpClient.get(
      `${this.getOrganization()}/config/allconfiguredmarketsppsi`
    );

  excludeIngredients = itemCodes =>
    this.httpClient.post(
      `dppsi/${this.getOrganization()}/ingredients/exclude`,
      itemCodes
    );

  includeIngredients = itemCodes =>
    this.httpClient.post(
      `dppsi/${this.getOrganization()}/ingredients/include`,
      itemCodes
    );

  // Images
  postImage = (file, thumbnailHeight, thumbnailWidth, action) => {
    const url = `dppsi/uploadImage?height=${thumbnailHeight}&width=${thumbnailWidth}${
      action ? `&action=${action}` : ''
    }`;

    return this.httpClient.post(url, file);
  };

  // PPI
  createPpi = (itemCode, ppi) =>
    this.httpClient.post(
      `dppsi/${this.getOrganization()}/ppis/create/${itemCode}`,
      ppi
    );

  getPpiBaseUrl = () => `dppsi/${this.getOrganization()}/ppis`;

  savePpi = (id, ppi) =>
    this.httpClient.put(`${this.getPpiBaseUrl()}/${id}`, ppi);

  fetchPpi = id => this.httpClient.get(`${this.getPpiBaseUrl()}/${id}`);

  fetchNewPPIAvilableData = id =>
    this.httpClient.get(`${this.getPpiBaseUrl()}/newglobalPPIupdate`);

  fetchCreateVariantPpi = (id, status) =>
    this.httpClient.get(
      `${this.getPpiBaseUrl()}/createVariant/${id}/${status}`
    );

  fetchPpis = filter => {
    const params = toParams(filter);
    return this.httpClient.get(`${this.getPpiBaseUrl()}${params}`);
  };

  fetchMarketUsedPpis = (marketCode, filter) => {
    const params = toParams(filter);
    // return this.httpClient.get(`${this.getPpiBaseUrl()}${params}`);
    return this.httpClient.get(`dppsi/${marketCode}/ppis${params}`);
  };

  fetchGlobalPpis = filter => {
    const params = toParams(filter);
    return this.httpClient.get(`${this.getPpiBaseUrl()}/global${params}`);
  };

  fetchPpisUpdate = filter => {
    return this.httpClient.get(`${this.getPpiBaseUrl()}/updates`);
  };

  fetchGlobalPpisUpdate = filter => {
    return this.httpClient.get(`${this.getPpiBaseUrl()}/update/global`);
  };

  fetchGlobalPpisWithId = id => {
    return this.httpClient.get(`${this.getPpiBaseUrl()}/global/${id}`);
  };

  publishPpi = (id, data) =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/${id}/publish`, data);

  unpublishPpi = id =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/${id}/unpublish`);

  createDraftPpi = id =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/${id}/createDraft`);

  discardDraftPpi = id =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/${id}/discardDraft`);

  // PPSI
  getPpsiBaseUrl = () => `dppsi/${this.getOrganization()}/ppsis`;

  createPpsi = (formulaCode, ppsi) =>
    this.httpClient.post(
      `${this.getPpsiBaseUrl()}?formulaCode=${formulaCode}`,
      ppsi
    );

  savePpsi = (id, ppsi) =>
    this.httpClient.put(`${this.getPpsiBaseUrl()}/${id}`, ppsi);

  fetchPpsi = id => this.httpClient.get(`${this.getPpsiBaseUrl()}/${id}`);

  deletePpsi = id =>
    this.httpClient.delete(`${this.getPpsiBaseUrl()}/deleteppsi`, { data: id });

  createVariantPpsi = (id, status) =>
    this.httpClient.get(
      `${this.getPpsiBaseUrl()}/createVariant/${id}/${status}`
    );

  deletePpi = id =>
    this.httpClient.delete(`dppsi/${this.getOrganization()}/ppis/deleteppi`, {
      data: id
    });

  fetchGlobalApprovalPpsi = (marketCode, id) =>
    this.httpClient.get(`dppsi/${marketCode}/ppsis/${id}`);

  fetchMarketPpi = marketCode =>
    this.httpClient.get(`dppsi/${marketCode}/ppis`);

  fetchPpsis = filter => {
    let params;
    if (filter.status && filter.relatedToPpiId === undefined) {
      delete filter.relatedToPpiId;
      params = toParams(filter);
    } else if (filter.status === undefined && filter.relatedToPpiId) {
      delete filter.status;
      delete filter.relatedToPpiId;
      params = toParams(filter);
    } else if (filter.status && filter.relatedToPpiId) {
      params = toParams(filter);
    } else {
      params = '';
    }
    return this.httpClient.get(`${this.getPpsiBaseUrl()}${params}`);
  };

  fetchPublishedUnpublishedPpsis = () => {
    return this.httpClient.get(
      `${this.getPpsiBaseUrl()}/copylocal?status=COPYLOCAL`
    );
  };

  //  fetch global review dishes and filter with status

  fetchReviewPpsis = (filter, marketCode) => {
    const params = toParams(filter);
    return this.httpClient.get(`dppsi/${marketCode}/ppsis${params}`);
  };

  //  fetch range Statistics dish report for markets

  fetchRangeStatistics = marketCode => {
    return this.httpClient.get(`dppsi/${marketCode}/ppsis/reports`);
  };

  fetchGlobalPpsi = id =>
    this.httpClient.get(`${this.getPpsiBaseUrl()}/global/${id}`);

  fetchGlobalPpsis = () =>
    this.httpClient.get(`${this.getPpsiBaseUrl()}/global`);

  fetchPpsisUpdates = () =>
    this.httpClient.get(`${this.getPpsiBaseUrl()}/updates`);

  fetchGlobalPpsisUpdates = () =>
    this.httpClient.get(`${this.getPpsiBaseUrl()}/update/global`);

  fetchGlobalNewPpsis = () =>
    this.httpClient.get(`${this.getPpsiBaseUrl()}/new/global`);

  fetchGlobalStatusPpsis = () =>
    this.httpClient.get(
      `dppsi/ppsis/${this.getOrganization()}/withGlobalStatus`
    );

  approvePpsi = (id, data) =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/approve`, data);

  globalApprovePpsi = (marketCode, id) =>
    this.httpClient.post(`dppsi/${marketCode}/ppsis/${id}/globalapprove`);

  globalRejectPpsi = (id, data) => {
    const marketCode = JSON.parse(localStorage.getItem('localMarket'));
    return this.httpClient.post(
      `dppsi/${marketCode}/ppsis/${id}/globalreject`,
      data
    );
  };

  sendForGlobalReviewPpsi = (id, data) =>
    this.httpClient.post(
      `${this.getPpsiBaseUrl()}/${id}/sendforglobalreview`,
      data
    );

  publishPpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/publish`);

  // IFPPSI-954 - Publish empty PPSI option for global admin;

  publishEmptyPpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/v2/approve`);

  unpublishPpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/unpublish`);

  createDraftPpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/createDraft`);

  discardDraftPpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/discardDraft`);

  // Utensils
  fetchUtensils = () =>
    this.httpClient.get(`dppsi/${this.getOrganization()}/utensils`);

  // Market Utensils
  fetchMarketUtensils = marketCode =>
    this.httpClient.get(`dppsi/${marketCode}/utensils`);

  fetchRecalledError = id =>
    this.httpClient.get(`dppsi/ppsis/${id}/ppsiEnrichmentStatus`);

  // Fetch global ppsi review summary
  fetchPpsisReviewSummary = market =>
    this.httpClient.get(
      `dppsi/ppsis/globalReviewSummary${market ? `?market=${market}` : ''}`
    );

  fetchGlobalUtensils = () =>
    this.httpClient.get(`dppsi/${globalLanguage}/utensils`);

  // Mark as uptoDate
  markAsUptoDatePpi = id =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/${id}/markUpToDate`);

  markAsUptoDatePpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/markUpToDate`);

  importUpdatePpi = id =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/${id}/importUpdates`);

  importiFoodUpdatePpsi = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/${id}/importUpdates`);

  importGlobalPpiUpdate = id =>
    this.httpClient.post(`${this.getPpiBaseUrl()}/ppiglobalimport/${id}`);

  importGlobalPpsiUpdate = id =>
    this.httpClient.post(`${this.getPpsiBaseUrl()}/globalimport/${id}`);

  //  fetch co-worker engagement report

  fetchCoWorkerEngagementReports = data => {
    return this.httpClient.get(
      `${data.market}/config/${data.year}/getcoworkerinteractioncountreport`
    );
  };

  fetchCoWorkerEngagementReportsMarketList = market => {
    // return this.httpClient.get(`dppsi/${market}/config/getcoworkerinteractionmarketreport`);
    return this.httpClient.get(
      `${this.getOrganization()}/config/getcoworkerinteractionmarketreport`
    );
  };

  fetchFeedback = data => {
    return this.httpClient.get(`/dppsi/${data}/getCoworkerFeedback`);
  };

  fetchMarketSetting = () =>
    this.httpClient.get(
      `${this.getOrganization()}/ppsimarketsettings/getPPSIMarketSetting`
    );

  fetchMarketDishLogo = () =>
    this.httpClient.get(
      `${this.getOrganization()}/ppsimarketsettings/getppsimarketlogos`
    );

  updateMarketSetting = data =>
    this.httpClient.put(
      `${this.getOrganization()}/ppsimarketsettings/updatePPSIMarketSetting`,
      data
    );

  highlightFeedback = data => {
    return this.httpClient.put(`/dppsi/${data}/highlightCoworkerFeedback`);
  };

  deleteFeedback = data => {
    return this.httpClient.put(`/dppsi/${data}/deleteCoworkerFeedback`);
  };

  fetchCoWorkerEngagementReports = data => {
    return this.httpClient.get(
      `${data.market}/config/${data.year}/getcoworkerinteractioncountreport`
    );
  };

  fetchCoWorkerEngagementReportsMarketList = market => {
    // return this.httpClient.get(`dppsi/${market}/config/getcoworkerinteractionmarketreport`);
    return this.httpClient.get(
      `${this.getOrganization()}/config/getcoworkerinteractionmarketreport`
    );
  };

  fetchGlobalPublishedPpsisNir = (status, toLocalLanguage) => {
    const params = { status, toLocalLanguage };

    const queryString = params ? toParams(params) : '';

    return this.httpClient.get(
      `/dppsi/${this.getOrganization()}/ppsis/existing/global${queryString}`
    );
  };

  fetchGlobalPublishedPpiIfs = id => {
    return this.httpClient.get(
      `/dppsi/IFS/ppis/existing/global?relatedToPPSI=${id}`
    );
  };

  saveIffLogin = () => this.httpClient.put(`/dppsi/savePPSILastLogin`);

  saveAvailabilityTab = (id, status, data) => {
    return this.httpClient.put(
      `/dppsi/${id}/modifyStoreAndSalesPoint/${status}`,
      data
    );
  };
  fetchFeedbackCount = () =>
    this.httpClient.get(
      `/dppsi/${this.getOrganization()}/getCoworkerFeedbackCount`
    );
  fetchGlobalPpiVariant = id =>
    this.httpClient.get(
      `/dppsi/${this.getOrganization()}/ppis/globalPpis/${id}`
    );
}
export default HttpDataApi;
